import React from 'react';
import PropTypes from 'prop-types';
import { useFormContext, useFieldArray } from 'react-hook-form';
import { Link } from 'react-router-dom';
import Isvg from 'react-inlinesvg';
import _get from 'lodash/get';
import { isBefore, parse, parseISO, startOfDay } from 'date-fns';

import { ICONS } from 'constants/icons';
import { SMALL_TEXT_MAX_CHARACTERS } from 'constants/logic';
import { parseNumber } from 'helpers/core';
import { bem } from 'helpers/class-name';
import { makePath } from 'helpers/link-generation';
import {
  AutocompleteTimePicker,
  Button,
  DateInput,
  Input,
  RadioGroup,
  RadioButton,
  Textarea,
} from 'components/HookForm';
import Message from 'components/Message';

import { AVAILABILITY_TYPES, DEFAULT_START_TIME, DEFAULT_END_TIME } from '../../ManageServices.constants';
import { messages } from './SingleDayExceptions.messages';
import './SingleDayExceptions.scss';

const { block, element } = bem('services-single-day-exceptions');

function SingleDayExceptions(props) {
  const { name, venueId } = props;
  const { control, watch, getValues } = useFormContext();
  const { fields, append, remove } = useFieldArray({ name, control, keyName: 'key' });

  return (
    <div className={block()}>
      <Message className={element('label')} as="div" message={messages.label} />
      <Message
        className={element('description')}
        as="div"
        message={messages.description}
        values={{ a: text => <Link to={makePath.editVenue(venueId, 'settings')}>{text}</Link> }}
      />
      {fields.map((field, idx) => {
        const {
          key,
          id,
          notEditableType,
          date,
          description,
          startTime,
          endTime,
          availability: defaultAvailability,
        } = field;
        const fieldName = `${name}[${idx}]`;
        const availability = watch(`${fieldName}.availability`);
        const isUnavailable = availability === AVAILABILITY_TYPES.UNAVAILABLE;

        const dateValidator = {
          message: messages.pastDate,
          value: value => {
            const parsedDate = parseISO(value);
            const today = new Date();
            return !isBefore(startOfDay(parsedDate), startOfDay(today));
          },
        };
        const timeValidator = {
          message: messages.startTimeAfterEndTime,
          value: () => {
            const values = getValues();
            const start = _get(values, `${fieldName}.startTime`);
            const end = _get(values, `${fieldName}.endTime`);
            const parsedStartTime = parse(start, 'HH:mm', new Date());
            const parsedEndTime = parse(end, 'HH:mm', new Date());
            return isBefore(parsedStartTime, parsedEndTime);
          },
        };

        return (
          <div key={key} className={element('group', 'grid grid--sm')}>
            <div className="grid__item 4/12 md-5/12">
              <Input
                type="hidden"
                name={`${fieldName}.id`}
                defaultValue={id}
                format={value => String(value) || ''}
                parse={value => parseNumber(value) || ''}
              />
              <DateInput name={`${fieldName}.date`} defaultValue={date} validate={dateValidator} className="mb-l" />
            </div>
            <div className="grid__item 3/12 md-2/12">
              <AutocompleteTimePicker
                name={`${fieldName}.startTime`}
                defaultValue={startTime}
                validate={timeValidator}
              />
            </div>
            <div className="grid__item 3/12 md-2/12">
              <AutocompleteTimePicker name={`${fieldName}.endTime`} defaultValue={endTime} validate={timeValidator} />
            </div>
            <Button
              className={element('delete-btn', 'grid__item 1/12 md-1/12 pt-1')}
              type={Button.TYPES.BUTTON}
              styled={false}
              onClick={() => remove(idx)}>
              <Isvg src={ICONS.trashCan} />
            </Button>
            <button
              type="button"
              className={element('grid__item 1/12 pb-1', 'p', 'pt-0')}
              data-cypress="add-exception-day-button"
              onClick={() =>
                append({
                  availability: AVAILABILITY_TYPES.UNAVAILABLE,
                  date: new Date().toISOString(),
                  startTime: DEFAULT_START_TIME,
                  endTime: DEFAULT_END_TIME,
                })
              }>
              <Isvg src={ICONS.addBlue} />
            </button>

            <div className="grid__item 1/1">
              <RadioGroup name={`${fieldName}.availability`} defaultValue={defaultAvailability} inline>
                <RadioButton disabled={notEditableType} value={AVAILABILITY_TYPES.AVAILABLE}>
                  <Message message={messages.available} />
                </RadioButton>
                <RadioButton disabled={notEditableType} value={AVAILABILITY_TYPES.UNAVAILABLE}>
                  <Message message={messages.unavailable} />
                </RadioButton>
              </RadioGroup>
            </div>
            {isUnavailable && (
              <div className="grid__item 1/1 mt-l">
                <Textarea
                  name={`${fieldName}.description`}
                  defaultValue={description}
                  maxLength={SMALL_TEXT_MAX_CHARACTERS}
                  label={messages.descriptionField}
                  heightInLines={3}
                  optional
                />
              </div>
            )}
          </div>
        );
      })}
      <div>
        <Message
          message={messages.add}
          as={Button}
          type={Button.TYPES.BUTTON}
          styled="tertiary"
          className={element('add-new')}
          onClick={() =>
            append({
              availability: AVAILABILITY_TYPES.UNAVAILABLE,
              date: new Date().toISOString(),
              startTime: DEFAULT_START_TIME,
              endTime: DEFAULT_END_TIME,
            })
          }
          data-cypress="add-exception-day-button"
        />
      </div>
    </div>
  );
}

SingleDayExceptions.propTypes = {
  name: PropTypes.string,
  venueId: PropTypes.number,
};

export default SingleDayExceptions;
